import { useEffect, useState } from "react"

import { App, Flex, Modal, Space, Spin, Tabs, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import ConnectMaverick from "../../Components/Forms/ConnectMaverick"
import ConnectPaymentProcessorForm from "../../Components/Forms/ConnectPaymentProcessorForm"
import LogsModal from "../../Components/Modal/LogsModal/LogsModal"
import Tour from "../../Components/Tour"
import {
  getMaverickTooltipContent,
  stepsTourConnectPaymentProcessor
} from "../../Components/Tour/stepsTourConnectPaymentProcessot"
import { RoutesPaths } from "../../constants/user-routes"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { buildSearchWithFlag, hasQueryFlag } from "../../Utils/queryFlags"
import utils, {
  handleViewTutorial,
  paymentProcessorDescription,
  paymentProcessorMap,
  paymentProcessorTitle,
  useQuery
} from "../../Utils/utils"
import TitleSection from "../BusinessSettings/components/TitleSection"
import { userActions } from "../User/redux/user.slice"
import {
  connectPaymentProcessor,
  disconnectPaymentProcessor,
  getBusinessAccount,
  getIntegrationLogs,
  getPaymentProcessors,
  requestNewIntegration
} from "../User/redux/user.thunks"
import { useUploadTransactions } from "./Adyen/hooks/useUploadTransactions"
import AdyenConnectionSuccessModal from "./Adyen/SuccessModal/AdyenConnectionSuccessModal"
import IntegrationCard from "./components/Card"
import IntegrationRequestForm from "./components/IntegrationRequestForm"
import RequestSuccess from "./components/RequestSuccess"
import ConnectNextcommerce from "./Nextcommerce/ConnectNextcommerce"
import useNextcommerceDisconnect from "./Nextcommerce/hooks/useNextcommerveDisconnect"
import { buildProcessors } from "./utils/buildProcessors"
import { shouldDisableManualButton } from "./utils/refundRules"

import "./styles.less"

const { Text, Title } = Typography
const INTEGRATIONS_LOGS_QUERY_PARAM = "integrations-logs"

// Tab grouping + per-tab display order. Cards are filtered/ordered from the
// built processor list by service_name; "Integration missing?" is appended to
// each tab. Labels go through t() (keys in en.json).
const INTEGRATION_TABS = [
  {
    key: "processors",
    label: "Payment processors",
    services: [
      "stripe",
      "shopify",
      "airwallex",
      "adyen",
      "authorize.net",
      "braintree",
      "nmi",
      "next",
      "maverick",
      "manual_refunds"
    ]
  },
  {
    key: "subscriptions",
    label: "Subscriptions",
    services: ["chargebee", "recurly", "recharge"]
  },
  {
    key: "notifications",
    label: "Notifications",
    services: ["slack", "zapier"]
  }
  // { key: 'mcp', label: 'MCP', services: ['claude', 'chatgpt', 'custom_mcp'] },
]

const dataRequestIntegration = {
  id: 7,
  isRequestIntegration: true,
  service_name: "missing",
  title: "Integration missing?",
  description: "Tell us which integration you need, and we’ll review your request",
  is_connected: false,
  isDisabled: false,
  disabledStatus: null,
  withoutPermission: true
}

const useIntegrationResultQuery = (search) => {
  const query = useQuery(search)
  return {
    integrationName: query.get("integration"), // slack
    integrationResult: query.get("integration_result"), //success or name_taken
    business_account_id: query.get("business_account_id")
  }
}

const IntegrationsPage = ({
  // Rendered as the Integrate tab of Alerts → Configure (CHA1-2847). The page
  // header and the document title then belong to ConfigurePage.
  embedded,
  usersViewForAdmin,
  nav: { theme, isMobile },
  isAdmin,
  isOneProcessorConnected,
  fetching,
  businessAccountFetching,
  saveFetching,
  userInfo,
  paymentProcessors,
  saveResult,
  businessAccount,
  integrationLogs,
  ...props
}) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { integrationName, integrationResult, business_account_id } =
    useIntegrationResultQuery(search)
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [connectPayment, setConnectPayment] = useState(null)

  const [openTour, setOpenTour] = useState(false)

  const [visible, setVisible] = useState(false)
  const [modalType, setModalType] = useState(null)

  const [customConnectServiceName, setCustomConnectServiceName] = useState(null)

  const [showAdyenSuccessModal, setShowAdyenSuccessModal] = useState(false)
  const isLogsModalOpen = hasQueryFlag(search, INTEGRATIONS_LOGS_QUERY_PARAM)

  let businessId = isAdmin ? business_account_id : userInfo?.["current_business_account_id"]
  let ba_role = isAdmin ? null : userInfo?.ba_role?.id

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  // Active tab is driven by the URL slug (/integrations/:page), mirroring the
  // Configure page. Falls back to the first tab for any unknown/empty slug.
  const { page } = useParams()
  const activeTab = INTEGRATION_TABS.some((tb) => tb.key === page) ? page : "processors"

  const processors = buildProcessors(paymentProcessors, {
    isAdmin,
    usersViewForAdmin,
    userInfo,
    businessAccount,
    paymentProcessorTitle,
    dataRequestIntegration
  })

  const disableManualButton = shouldDisableManualButton(processors)
  // Non-admin: split into tabs, ordered per INTEGRATION_TABS, with the
  // "Integration missing?" card appended to each tab. Admin view keeps the
  // existing single connected-only grid.
  const requestCard = processors.find((p) => p.isRequestIntegration) || dataRequestIntegration
  const tabServices = INTEGRATION_TABS.find((tb) => tb.key === activeTab)?.services ?? []
  const orderedForTab = tabServices
    .map((sn) => processors.find((p) => p.service_name === sn))
    .filter(Boolean)
  const visibleProcessors = isAdmin ? processors : [...orderedForTab, requestCard]

  const renderConnectFormComponent = (connectPayment, businessId, handlers) => {
    const commonProps = {
      fields: connectPayment?.["list_of_required_fields_for_tokens"]?.filter(
        (el) => el !== "merchant_id"
      ),
      onClose: handlers.handleCloseModal,
      service_name: connectPayment?.service_name,
      disabled: handlers.businessAccountFetching,
      error: handlers.saveResult?.["connect_error"],
      extraBtn: (
        <TutorialButton
          serviceName={connectPayment?.service_name}
          onOpenTour={handlers.handleOpenTour}
          onViewTutorial={handleViewTutorial}
          isMobile={isMobile}
        />
      )
    }

    if (connectPayment?.service_name === "maverick") {
      return <ConnectMaverick {...commonProps} merchant_id={businessId} isMobile={isMobile} />
    }

    return (
      <ConnectPaymentProcessorForm
        {...commonProps}
        onSubmit={handlers.handleConnectPaymentWithForm}
        isMobile={isMobile}
      />
    )
  }

  useEffect(() => {
    if (businessId) {
      props.getBusinessAccount(businessId)
    }
    props.getPaymentProcessors({
      integrationsPage: true,
      merchant_id: businessId,
      return_url: process.env.REACT_APP_BASE_URI + pathname
    })
    if (!embedded) document.title = "Integrations - Chargeback"
    // props.getBusinessAccount(businessId)
    //props.getDashboardInfo({})
    // props.getBusinessAccount(businessId)
  }, [businessId])

  useEffect(() => {
    props.getIntegrationLogs()
  }, [])

  useEffect(() => {
    if (
      ["connected", "disconnected", "request_new_integration"]?.includes(saveResult) ||
      integrationName
    ) {
      let nameIntegration = connectPayment?.service_name || integrationName
      let namePayment =
        paymentProcessorMap?.[nameIntegration] || utils.toUpperLatter(nameIntegration)
      let message = null
      let description = null

      if (integrationResult) {
        if (integrationResult === "success") {
          notification.success({
            message: t("_namePayment_ was connected successfully.", { namePayment }),
            ...notificationConfig
          })
        } else if (integrationResult === "error") {
          notification.error({
            message: t("We encountered issues integrating Slack."),
            description:
              "Please try setting up the Slack integration again, or contact Chargeback Support for assistance with connection issues.",
            ...notificationConfig,
            duration: 10
          })
        }
        if (integrationResult)
          navigate(
            business_account_id
              ? `${pathname}?${new URLSearchParams({ business_account_id })}`
              : pathname,
            { replace: true }
          )
      } else {
        if (connectPayment) {
          if (saveResult === "connected") {
            message = t("Payment processor connected")
            description = t("_namePayment_ was connected successfully.", { namePayment })
          } else if (saveResult === "disconnected") {
            message = t("Payment processor disconnected")
            description = t("_namePayment_ was disconnected.", { namePayment })

            if (connectPayment?.service_name === "slack") {
              message = t("_namePayment_ was disconnected.", { namePayment })
              description = ""
            }

            if (
              (!isOneProcessorConnected && connectPayment.service_name !== "manual_refunds") ||
              ["slack", "premium_support", "full"].includes(connectPayment.service_name)
            ) {
              props.getPaymentProcessors({
                integrationsPage: true,
                merchant_id: businessId,
                return_url: process.env.REACT_APP_BASE_URI + pathname
              })
            }
          }
          if (connectPayment?.service_name === "adyen" && saveResult === "connected") {
            setShowAdyenSuccessModal(true)
          } else {
            notification.success({
              message,
              description,
              ...notificationConfig
            })
          }

          handleCloseModal()
          props.userSaveResultReset()
          props.getIntegrationLogs()
        }
        if (saveResult === "request_new_integration") {
          setModalType("success")
          props.userSaveResultReset()
          props.getIntegrationLogs()
        }
      }
      props.getBusinessAccount(businessId)
    }
  }, [saveResult, isOneProcessorConnected, integrationName, integrationResult])

  const handleConnectPaymentWithForm = (data) => {
    if (!connectPayment) return
    props.connectPaymentProcessor({
      service_name: connectPayment.service_name,
      merchant_id: connectPayment.merchant_id,
      ...data
    })
  }

  const { disconnectNext } = useNextcommerceDisconnect()

  const disconnectPayment = (payment) => {
    const namePayment =
      paymentProcessorMap[payment.service_name] || utils.toUpperLatter(payment.service_name)

    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: t("Disconnect _namePayment_?", { namePayment }),
      content:
        payment.service_name === "stripe"
          ? t(
              `If you disconnect this integration, Chargeback.io will stop processing alerts and cancelling subscriptions through Stripe. This action can't be undone.`
            )
          : t(
              `If you disconnect this integration, Chargeback.io will stop processing alerts through _namePayment_. This action can't be undone.`,
              { namePayment }
            ),

      cancelText: t("Cancel"),
      okText: t("Disconnect"),
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        danger: true,
        ghost: true
      },
      onOk() {
        if (payment.service_name === "next") {
          disconnectNext(businessId)
          return
        }
        props.disconnectPaymentProcessor({
          payment_processor_id: payment.id,
          service_name: payment.service_name,
          business_id: businessId
        })
      }
    })
  }

  const handleClick = (payment) => {
    if (payment.service_name === "stripe" && payment.has_invalid_token) {
      window.open("https://marketplace.stripe.com/", "_blank", "noopener,noreferrer")
      return
    }
    if (payment.isRequestIntegration) {
      setModalType("integrationRequest")
      setVisible(true)
    } else if (payment["is_connected"]) {
      setConnectPayment(payment)
      disconnectPayment(payment)
      /*if(isAdmin || ['stripe', 'shopify']?.includes(payment?.['service_name'])){
        setConnectPayment(payment)
        disconnectPayment(payment)
      }else{
        return null
      }*/
    } else if (payment?.service_name === "next") {
      setCustomConnectServiceName(payment.service_name)
    } else {
      if (!payment.link && payment?.["list_of_required_fields_for_tokens"].length) {
        setConnectPayment(payment)
        setOpenTour(true)
        //setModalType('connectForm')
        //setVisible(true)
      } else {
        if (payment.service_name === "manual_refunds") {
          setConnectPayment(payment)
          props.connectPaymentProcessor({
            service_name: payment.service_name,
            payment_processor_id: payment.id
          })
        } else {
          if (payment.link) utils.goLink(payment.link)
          //handleConnectPayment(payment.service_name)
        }
      }
    }
  }

  const handleCloseTour = (closedBy) => {
    if (closedBy === "mask") {
      setOpenTour(false)
      return
    }
    setOpenTour(false)
    setModalType("connectForm")
    setVisible(true)
  }

  const handleCloseModal = () => {
    setVisible(false)
    setModalType(null)
    setConnectPayment(null)
    if (saveResult?.["connect_error"]) props.userSaveResultReset()
  }

  const handleCloseLogsModal = () => {
    navigate({
      pathname,
      search: buildSearchWithFlag(search, INTEGRATIONS_LOGS_QUERY_PARAM, false)
    })
  }

  const handleOpenTour = () => {
    setOpenTour(true)
    setModalType(null)
    setVisible(false)
  }

  const { handleUploadFiles, adyenFilesUploading } = useUploadTransactions({
    businessId,
    notification
  })

  const TutorialButton = ({ serviceName, isMobile }) => {
    const isAdyen = serviceName === "adyen"

    return (
      <>
        {!isMobile ? (
          <ButtonComponent
            type="text"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            icon={isAdyen ? null : <Icon type="arrow_left" />}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        ) : (
          <ButtonComponent
            htmlType="button"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        )}
      </>
    )
  }

  const handleSubmit = (data) => {
    props.sendRequestNewIntegration(data)
    //setModalType('success')
  }

  const integrationRequestBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {t("Tell us which integration you need, and we’ll review your request")}
      </Text>

      <IntegrationRequestForm
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        loading={saveFetching && modalType === "integrationRequest"}
      />
    </div>
  )

  const modalBlocks = {
    integrationRequest: {
      title: t("Integration request"),
      component: integrationRequestBlock
    },
    success: {
      title: null,
      component: <RequestSuccess onClose={handleCloseModal} />
    },
    connectForm: {
      title: (
        <Space direction="vertical">
          <Text className="payment-connect-title">
            {t("Connect _namePayment_", {
              namePayment: paymentProcessorMap[connectPayment?.["service_name"]]
            })}
          </Text>
          <Text className="payment-connect-sub_title">
            {t(
              paymentProcessorDescription[connectPayment?.["service_name"]] ??
                "Connect your _namePayment_ account to automate refunds for incoming alerts",
              { namePayment: paymentProcessorMap[connectPayment?.["service_name"]] }
            )}
          </Text>
        </Space>
      ),
      component: renderConnectFormComponent(connectPayment, businessId, {
        handleCloseModal,
        businessAccountFetching,
        saveResult,
        handleConnectPaymentWithForm,
        handleOpenTour
      })
    }
  }

  return (
    <>
      {isAdmin && (
        <div style={usersViewForAdmin ? { padding: "0 32px" } : {}}>
          <TitleSection
            title={`Integrations for ${businessAccount?.name}`}
            link={businessAccount?.url}
            queryParam={INTEGRATIONS_LOGS_QUERY_PARAM}
            queryButtonText="View logs"
          />
        </div>
      )}
      <Spin size="large" spinning={fetching || saveFetching}>
        <div
          className={cls("integrations-page", {
            "integrations-page_admin": isAdmin,
            "integrations-page_users_view_for_admin": usersViewForAdmin
          })}
        >
          {!isAdmin && (
            <div className="integrations-tabs">
              <Tabs
                items={INTEGRATION_TABS.map((tb) => ({
                  key: tb.key,
                  label: t(tb.label)
                }))}
                activeKey={activeTab}
                onChange={(val) => navigate(`${RoutesPaths.configure.root}/${val}`)}
              />
            </div>
          )}
          <Flex gap={isAdmin ? 16 : 24} wrap={"wrap"}>
            {visibleProcessors.map((el) => {
              if (!el.service_name) return null

              const disableManual = el.service_name === "manual_refunds" && disableManualButton

              return (
                <IntegrationCard
                  key={el.service_name}
                  data={el}
                  onClick={handleClick}
                  className={cls({ "integration-card_admin": isAdmin })}
                  theme={theme}
                  baRole={ba_role}
                  disabled={disableManual || !businessId}
                  uploadFiles={
                    el.service_name === "adyen" &&
                    el.is_connected &&
                    !el.transactions_uploaded &&
                    isAdmin
                  }
                  onUpload={handleUploadFiles}
                  uploading={adyenFilesUploading}
                  tooltip={getMaverickTooltipContent}
                />
              )
            })}
          </Flex>

          <Tour
            open={openTour}
            steps={stepsTourConnectPaymentProcessor()[connectPayment?.service_name]}
            className={cls("tour-with-skip")}
            onClose={handleCloseTour}
            key={connectPayment?.service_name}
            isClosableOnMaskClick={true}
            indicatorsRender={() => (
              <ButtonComponent size="large" onClick={handleCloseTour}>
                Skip tutorial
              </ButtonComponent>
            )}
          />

          <Modal
            {...(isMobile ? { transitionName: "" } : null)}
            open={visible}
            centered={!isMobile}
            width={442}
            title={modalBlocks[modalType]?.title}
            footer={null}
            closeIcon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
            closable="true"
            onCancel={handleCloseModal}
            destroyOnClose
            classNames={{
              content: cls("clear-bg full-width-header", {
                "bg-center": modalType === "success",
                "payment-connect": modalType === "connectForm",
                recurly: connectPayment?.["service_name"] === "recurly"
              }),
              header: cls({
                "payment-connect": modalType === "connectForm",
                [connectPayment?.["service_name"]]: modalType === "connectForm"
              })
            }}
          >
            <Spin size="large" spinning={fetching} wrapperClassName="visible">
              {modalBlocks[modalType]?.component}
            </Spin>
          </Modal>

          <AdyenConnectionSuccessModal
            opened={showAdyenSuccessModal}
            close={() => setShowAdyenSuccessModal(false)}
          />
          <LogsModal
            title={"Logs"}
            description={"View the latest activity for integrations."}
            logs={integrationLogs}
            open={isLogsModalOpen}
            onClose={handleCloseLogsModal}
          />
          {customConnectServiceName === "next" && (
            <ConnectNextcommerce
              onFinish={() => setCustomConnectServiceName(null)}
              isMobile={isMobile}
            />
          )}
        </div>
      </Spin>
    </>
  )
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  nav: state.nav,

  fetching: state.user.fetching,
  businessAccountFetching: state.user.businessAccountFetching,
  paymentProcessors: state.user.paymentProcessors,
  businessAccount: state.user.businessAccount,
  integrationLogs: state.user.integrationLogs,

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,

  isOneProcessorConnected: state.user.isOneProcessorConnected
})

const mapDispatchToProps = (dispatch) => ({
  getBusinessAccount: (businessAccountId) => dispatch(getBusinessAccount(businessAccountId)),
  getPaymentProcessors: (data) => dispatch(getPaymentProcessors(data)),
  connectPaymentProcessor: (data) => dispatch(connectPaymentProcessor(data)),
  sendRequestNewIntegration: (data) => dispatch(requestNewIntegration(data)),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),

  disconnectPaymentProcessor: (data) => dispatch(disconnectPaymentProcessor(data)),
  getIntegrationLogs: () => dispatch(getIntegrationLogs())
})

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationsPage)
